/**
 * Settings page section constants.
 *
 * The active settings section is passed through React Router navigation `state`
 * so it survives a full remount of {@link SettingsPage} — e.g. when returning
 * from the out-of-AppShell map configurator (`/w/:shortId/map-config`) — while
 * keeping the URL clean. Keeping these ids in one place lets navigators (the
 * map configurator) target a specific section without re-hardcoding the
 * strings.
 *
 * @module pages/SettingsPage/constants
 */

/** Canonical settings section ids (must match {@link SettingsPage} `navItems`). */
export const SETTINGS_SECTIONS = {
  personal: 'personal',
  emailPassword: 'email-password',
  location: 'location',
  workspace: 'workspace',
} as const;

/** One of the canonical settings section ids. */
export type SettingsSection =
  (typeof SETTINGS_SECTIONS)[keyof typeof SETTINGS_SECTIONS];

/** Section shown when no (or an unknown) section is requested on entry. */
const DEFAULT_SETTINGS_SECTION: SettingsSection = SETTINGS_SECTIONS.personal;

/**
 * Router navigation `state` shape understood by {@link SettingsPage}. Navigators
 * set `section` to land the user on a specific section on entry.
 */
export interface SettingsLocationState {
  section?: SettingsSection;
}

const SETTINGS_SECTION_VALUES = Object.values(
  SETTINGS_SECTIONS
) as SettingsSection[];

/**
 * Resolve an untrusted section value (from navigation state) to a valid
 * section, falling back to {@link DEFAULT_SETTINGS_SECTION} for missing/unknown
 * input.
 */
export function resolveSettingsSection(
  value: string | null | undefined
): SettingsSection {
  return SETTINGS_SECTION_VALUES.includes(value as SettingsSection)
    ? (value as SettingsSection)
    : DEFAULT_SETTINGS_SECTION;
}
